import { Injectable } from '@angular/core';
import { SpacesClientService } from '@mode/shared/data-access-webapp';
import { AccountClientService } from '@mode/shared/data-access-webapp';
import {
  CollectionUpdateErrorEvent,
  CollectionUpdateSuccessEvent,
  EntitlementUpdateErrorEvent,
  EntitlementUpdateSuccessEvent,
  CollectionAccessLevel,
} from '@mode/shared/contract-common';
import { SpaceDomain } from '@mode/shared/contract-common';
import { asyncScheduler, BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, delay, distinctUntilChanged, filter, map, tap, withLatestFrom } from 'rxjs/operators';
import { isPresent } from '@mode/shared/util-js';
import { AccountFacade } from '@mode/accounts/contract';

interface SearchResult {
  query: string;
  results: SpaceDomain.PermissionsSearchResult[];
}

@Injectable()
export class CollectionManageAccessFacade {
  protected entitlementStream = new BehaviorSubject<SpaceDomain.Entitlement[] | undefined>(undefined);
  protected entitlementErroredStream = new BehaviorSubject<boolean | undefined>(undefined);
  protected entitlementUpdateStream = new BehaviorSubject<SpaceDomain.Entitlement[] | undefined>(undefined);
  protected searchResultsStream = new BehaviorSubject<SearchResult | undefined>(undefined);
  protected entitlementUpdateErrorStream = new BehaviorSubject<EntitlementUpdateErrorEvent | undefined>(undefined);
  protected entitlementUpdateSuccessStream = new BehaviorSubject<EntitlementUpdateSuccessEvent | undefined>(undefined);
  protected entitlementDeleteSuccessStream = new BehaviorSubject<EntitlementUpdateSuccessEvent | undefined>(undefined);
  protected defaultAccessUpdateErrorStream = new BehaviorSubject<CollectionUpdateErrorEvent | undefined>(undefined);
  protected defaultAccessUpdateSuccessStream = new BehaviorSubject<CollectionUpdateSuccessEvent | undefined>(undefined);
  protected spaceStream = new BehaviorSubject<SpaceDomain.Space | undefined>(undefined);

  public defaultAccessLevel$: Observable<CollectionAccessLevel | undefined>;
  public entitlement$: Observable<SpaceDomain.Entitlement[] | undefined>;
  public entitlementErrored$: Observable<boolean | undefined>;
  public explicitEntitlements$: Observable<Array<SpaceDomain.Entitlement>>;
  public loadingEntitlements$ = new BehaviorSubject(false);
  public searchResults$: Observable<SearchResult | undefined>;
  public entitlementUpdateError$: Observable<EntitlementUpdateErrorEvent>;
  public entitlementUpdateSuccess$: Observable<EntitlementUpdateSuccessEvent>;
  public entitlementDeleteSuccess$: Observable<EntitlementUpdateSuccessEvent>;
  public canDestroyEntitlements$: Observable<boolean | undefined>;
  public defaultAccessUpdateSuccess$: Observable<CollectionUpdateSuccessEvent>;
  public defaultAccessUpdateError$: Observable<CollectionUpdateErrorEvent>;

  constructor(
    private spacesClientService: SpacesClientService,
    private accountClientService: AccountClientService,
    private accountFacade: AccountFacade
  ) {
    this.entitlement$ = this.entitlementStream.asObservable();
    this.entitlementErrored$ = this.entitlementErroredStream.asObservable();
    this.searchResults$ = this.searchResultsStream.asObservable();
    this.entitlementUpdateError$ = this.entitlementUpdateErrorStream.pipe(filter(isPresent));
    this.entitlementUpdateSuccess$ = this.entitlementUpdateSuccessStream.pipe(filter(isPresent));
    this.entitlementDeleteSuccess$ = this.entitlementDeleteSuccessStream.pipe(filter(isPresent));
    this.defaultAccessUpdateSuccess$ = this.defaultAccessUpdateSuccessStream.pipe(filter(isPresent));
    this.defaultAccessUpdateError$ = this.defaultAccessUpdateErrorStream.pipe(filter(isPresent));
    this.canDestroyEntitlements$ = this.spaceStream.pipe(
      filter(isPresent),
      map(({ canDestroyEntitlements }) => canDestroyEntitlements)
    );
    this.defaultAccessLevel$ = this.spaceStream.pipe(
      filter(isPresent),
      map(({ defaultAccessLevel }) => defaultAccessLevel),
      distinctUntilChanged()
    );

    // explicit entitlements are the additional access entitlements on top of the general access
    this.explicitEntitlements$ = this.entitlement$.pipe(
      filter(isPresent),
      map((entitlements) => entitlements.filter((entitlement) => entitlement.groupType !== 'everyone'))
    );
  }

  private notifyDefaultAccessUpdateSuccess(collectionToken: string) {
    // notify asynchronously to be used safely with synchronous code
    asyncScheduler.schedule(() => {
      this.defaultAccessUpdateSuccessStream.next({ collectionToken });

      // reset asynchronously after animation starts to prevent ghost notifications
      asyncScheduler.schedule(() => this.defaultAccessUpdateSuccessStream.next(undefined));
    });
  }

  private notifyEntitlementUpdateSuccess(entitlementToken: string) {
    // notify asynchronously to be used safely with synchronous code
    asyncScheduler.schedule(() => {
      this.entitlementUpdateSuccessStream.next({ entitlementToken });

      // reset asynchronously after animation starts to prevent ghost notifications
      asyncScheduler.schedule(() => this.entitlementUpdateSuccessStream.next(undefined));
    });
  }

  private notifyEntitlementDeleteSuccess(entitlementToken: string) {
    // notify asynchronously to be used safely with synchronous code
    asyncScheduler.schedule(() => {
      this.entitlementDeleteSuccessStream.next({ entitlementToken });

      // reset asynchronously after animation starts to prevent ghost notifications
      asyncScheduler.schedule(() => this.entitlementDeleteSuccessStream.next(undefined));
    });
  }

  private optimisticallyUpdateEntitlements(
    oldEntitlements: SpaceDomain.Entitlement[] | undefined,
    entitlementToken: string,
    entitlementAction: string
  ) {
    const resolveAction = (ent: SpaceDomain.Entitlement) =>
      ent.token === entitlementToken ? (entitlementAction as CollectionAccessLevel) : ent.action;
    const newEntitlements = oldEntitlements?.map(
      (entitlement) =>
        ({
          ...entitlement,
          action: resolveAction(entitlement),
        }) as SpaceDomain.Entitlement
    );
    this.entitlementUpdateStream.next(newEntitlements);
  }

  private enableEntitlementsLoading() {
    this.loadingEntitlements$.next(true);
  }

  private disableEntitlementsLoading() {
    this.loadingEntitlements$.next(false);
  }

  loadSpace(accountId: string, spaceToken: string): void {
    this.spacesClientService.getSpace(accountId, spaceToken).subscribe((space) => this.spaceStream.next(space));
  }

  createEntitlement(
    accountId: string,
    spaceToken: string,
    entitlementAction: string,
    memberToken: string,
    memberType: 'User' | 'UserGroup'
  ): void {
    this.spacesClientService
      .createSpaceEntitlement(accountId, spaceToken, entitlementAction, memberToken, memberType)
      .pipe(
        withLatestFrom(this.entitlementStream),
        map(
          ([newEntitlement, current]): {
            entitlements: SpaceDomain.Entitlement[] | undefined;
            newEntitlementToken: string;
          } => ({
            entitlements: current?.concat(newEntitlement),
            newEntitlementToken: newEntitlement.token,
          })
        )
      )
      .subscribe(({ entitlements, newEntitlementToken }) => {
        this.notifyEntitlementUpdateSuccess(newEntitlementToken);

        this.entitlementUpdateStream.next(entitlements);
        // sync the streams immediately to make sure new tokens are available immediately
        this.updateEntitlementStream();
      });
  }

  clearEntitlements() {
    this.entitlementStream.next([]);
    this.entitlementUpdateStream.next([]);
  }

  clearUpdateSuccessAndErrors() {
    this.entitlementUpdateSuccessStream.next(undefined);
    this.entitlementDeleteSuccessStream.next(undefined);
    this.entitlementUpdateErrorStream.next(undefined);

    this.defaultAccessUpdateSuccessStream.next(undefined);
    this.defaultAccessUpdateErrorStream.next(undefined);
  }

  deleteAllEntitlements(accountId: string, spaceToken: string) {
    this.enableEntitlementsLoading();
    this.spacesClientService.deleteAllSpaceEntitlements(accountId, spaceToken).subscribe(() => {
      this.getEntitlements(accountId, spaceToken);
    });
  }

  deleteEntitlement(accountId: string, spaceToken: string, entitlementToken: string): void {
    const isNotEntitlement = (entitlement: SpaceDomain.Entitlement, token: string) => entitlement.token !== token;
    const omitEntitlement = (entitlements: SpaceDomain.Entitlement[] | undefined, token: string) =>
      entitlements?.filter((entitlement) => isNotEntitlement(entitlement, token));

    this.spacesClientService
      .deleteSpaceEntitlement(accountId, spaceToken, entitlementToken)
      .pipe(
        withLatestFrom(this.entitlementStream),
        tap(() => this.notifyEntitlementDeleteSuccess(entitlementToken)),
        map(([_, entitlements]) => omitEntitlement(entitlements, entitlementToken))
      )
      .subscribe((entitlements) => {
        this.entitlementUpdateStream.next(entitlements);
      });
  }

  getEntitlements(accountId: string, spaceToken: string): void {
    this.enableEntitlementsLoading();
    this.spacesClientService
      .getEntitlements(accountId, spaceToken)
      .pipe(
        catchError((err) => {
          this.entitlementErroredStream.next(true);
          return of([]);
        })
      )
      .subscribe((entitlements: SpaceDomain.Entitlement[]) => {
        this.entitlementStream.next(entitlements);
        this.disableEntitlementsLoading();
      });
  }

  searchUnentitledAccessors(accountId: string, spaceToken: string, query = ''): void {
    this.spacesClientService
      .searchUnentitledSpaceMembers(accountId, spaceToken, query)
      .subscribe((results: SpaceDomain.PermissionsSearchResult[]) => {
        this.searchResultsStream.next({ query, results });
      });
  }

  updateDefaultAccessLevel(accountId: string, collectionToken: string, accessLevel: CollectionAccessLevel) {
    this.spacesClientService
      .updateDefaultAccessLevel(accountId, collectionToken, accessLevel)
      .pipe(
        tap(() => {
          this.notifyDefaultAccessUpdateSuccess(collectionToken);
          // Also update account collections as well after creating space.
          // This refreshes the navmenu sidebar list of collections.
          // This can move to an effect if space client service actions
          // are moved to ngrx.
          this.accountFacade.refreshCollections();
        }),
        catchError((err) => {
          return of(err).pipe(
            // show error
            tap((error) => this.defaultAccessUpdateErrorStream.next({ collectionToken, error })),
            // allow time for animation
            delay(1000),
            // clear error
            tap(() => this.defaultAccessUpdateErrorStream.next(undefined))
          );
        })
      )
      .subscribe((collection: SpaceDomain.Space | undefined) => {
        if (collection) {
          this.spaceStream.next(collection);
        }
      });
  }

  updateEntitlement(accountId: string, spaceToken: string, entitlementToken: string, entitlementAction: string): void {
    const oldEntitlements: SpaceDomain.Entitlement[] | undefined = this.entitlementStream.getValue();
    this.optimisticallyUpdateEntitlements(oldEntitlements, entitlementToken, entitlementAction);

    this.spacesClientService
      .updateSpaceEntitlement(accountId, spaceToken, entitlementToken, entitlementAction)
      .pipe(
        withLatestFrom(this.entitlementStream),
        map(([updated, entitlements]) =>
          entitlements?.map((entitlement) => {
            if (entitlement.token === updated.token) {
              return updated;
            } else {
              return entitlement;
            }
          })
        ),
        tap(() => this.notifyEntitlementUpdateSuccess(entitlementToken)),
        catchError((err) =>
          // return old entitlements and notify user of error
          of(oldEntitlements).pipe(
            // show error
            tap(() => this.entitlementUpdateErrorStream.next(err)),
            // allow time for animation
            delay(1000),
            // clear error
            tap(() => this.entitlementUpdateErrorStream.next(undefined))
          )
        )
      )
      .subscribe((entitlements: SpaceDomain.Entitlement[] | undefined) => {
        this.entitlementUpdateStream.next(entitlements);
      });
  }

  updateEntitlementStream() {
    const currentEntitlements = this.entitlementUpdateStream.getValue();

    this.entitlementStream.next(currentEntitlements);
  }
}
